import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import logger from '@adc/logger'

/**
 * Returns a debounced copy of `value` that only updates after `delay` ms of
 * no changes. Keeps a single lodash-debounced setter behind the hook so
 * consumers pass a raw value in and read a stable debounced value out —
 * no debounce wiring at the call site.
 */
export const useDebounce = <T>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value)

  const update = useMemo(
    () => _.debounce((next: T) => setDebounced(next), delay),
    [delay]
  )

  useEffect(() => {
    update(value)
    return () => update.cancel()
  }, [value, update])

  return debounced
}

/**
 * Guards async flows against out-of-order responses. Call the returned fn at
 * the start of each request to claim a monotonic id; it hands back an
 * `isCurrent()` checker. Only the latest claimed request reports current, so
 * stale responses (a slow earlier request resolving after a newer one) can be
 * dropped instead of overwriting fresh state.
 *
 *   const latest = useLatestRequest()
 *   const isCurrent = latest()
 *   fetchThing().then(r => { if (!isCurrent()) return; setState(r) })
 */
export const useLatestRequest = (): (() => () => boolean) => {
  const requestIdRef = useRef(0)
  return useCallback(() => {
    const id = ++requestIdRef.current
    return () => id === requestIdRef.current
  }, [])
}

const initialDownloadState = {
  content: [],
  totalRetrieved: 0,
}

export const useDownloadActions = (
  onFetchData: (...params: any) => Promise<any>,
  totalCount: number,
  filters: any = {},
  parseContent?: (response: any[]) => any[]
) => {
  const [downloadData, setDownloadData] = useState<any>(initialDownloadState)
  const isDownloadAborted = useRef(false)

  const handleAbort = useCallback(() => (isDownloadAborted.current = true), [])

  const handleReset = useCallback(
    () => setDownloadData(initialDownloadState),
    []
  )

  const handleDownloadData = useCallback(async () => {
    setDownloadData({
      ...initialDownloadState,
      totalCount,
      isLoading: true,
    })
    isDownloadAborted.current = false
    try {
      const response = await onFetchData({
        page: 0,
        ...filters,
      })
      const content = parseContent
        ? parseContent(response.content)
        : response.content
      if (isDownloadAborted.current) {
        return handleReset()
      }
      setDownloadData((prevData: any) => ({
        ...prevData,
        content: [...prevData.content, ...content],
        totalRetrieved: content.length,
        isLoading: false,
        isBatchSuccessful: true,
      }))
    } catch (error: any) {
      logger.error('Error downloading data', error)
      const message =
        error.message ||
        error.statusText ||
        (error.body && (error.body.title || error.body.message)) ||
        error.toString()
      setDownloadData((prevData: any) => ({
        ...prevData,
        isLoading: false,
        isBatchSuccessful: false,
        errorInfo: {
          status: error.status || 520,
          statusText: message || 'something went wrong',
        },
      }))
    }
  }, [filters, handleReset, onFetchData, totalCount])

  const downloadActions = useMemo(
    () => ({
      fetchData: handleDownloadData,
      abort: handleAbort,
      reset: handleReset,
    }),
    [handleAbort, handleDownloadData, handleReset]
  )

  return { downloadActions, downloadData }
}
